<!--
  @project E-School Web
  @author Vothana CHY
  @date 2026-07-13
-->

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import imgSocial from '~/assets/mobile/1. social-page.png';
import imgVideo from '~/assets/mobile/2. video-page.png';
import imgZoom from '~/assets/mobile/3. zoom-page.png';
import imgLibrary from '~/assets/mobile/4. library-page.png';
import imgQuiz from '~/assets/mobile/5. quiz-page.png';

const { t } = useI18n();

const currentVirtualIndex = ref(7); // Start at the Zoom screen (index 2) in the middle set (index 7)
const scrollContainer = ref<HTMLDivElement | null>(null);
let slideTimer: ReturnType<typeof setInterval> | null = null;
let isProgrammaticScrolling = false;

// Compute actual selected screen index (0 to 4) for bottom dot highlights
const currentIndex = computed(() => {
  return currentVirtualIndex.value % 5;
});

const replicatedApps = computed(() => {
  const rawApps = [
    {
      id: 1,
      label: t('mobile.screens.social'),
      img: imgSocial,
    },
    {
      id: 2,
      label: t('mobile.screens.video'),
      img: imgVideo,
    },
    {
      id: 3,
      label: t('mobile.screens.zoom'),
      img: imgZoom,
    },
    {
      id: 4,
      label: t('mobile.screens.library'),
      img: imgLibrary,
    },
    {
      id: 5,
      label: t('mobile.screens.quiz'),
      img: imgQuiz,
    },
  ];

  // Repeat list 3 times to make scrolling truly infinite
  const repeated = [...rawApps, ...rawApps, ...rawApps];
  return repeated.map((app, idx) => ({
    ...app,
    uniqueId: `${app.id}-${idx}`,
    isCenter: idx === currentVirtualIndex.value,
  }));
});

// ─── Slide timer ──────────────────────────────────────────────────────────────

const startSlideTimer = () => {
  stopSlideTimer();
  slideTimer = setInterval(() => {
    currentVirtualIndex.value = (currentVirtualIndex.value + 1) % 15;
    scrollToActive();
  }, 5000);
};

const stopSlideTimer = () => {
  if (slideTimer) {
    clearInterval(slideTimer);
    slideTimer = null;
  }
};

const stopProgrammaticScrollAnimation = () => {
  if (!scrollContainer.value) return;
  isProgrammaticScrolling = false;
  const current = scrollContainer.value.scrollLeft;
  // Setting scrollLeft to itself with behavior 'auto' halts any active smooth scroll animation instantly
  scrollContainer.value.style.scrollBehavior = 'auto';
  scrollContainer.value.scrollLeft = current;
  scrollContainer.value.style.scrollBehavior = '';
};

// ─── Reusable auto-play pause/resume with swipe threshold ────────────────────
// isDragging shared with useDragToScroll so mouseleave during a drag
// does NOT schedule a resume (fixes "keeps animating while holding" bug).

const isDragging = ref(false);

const { touchHandlers, mouseHandlers, cancelResume, pauseAndScheduleResume, notifyScrolling } = useCarouselAutoPlay(
  startSlideTimer,
  stopSlideTimer,
  {
    resumeDelay: 3000,
    isDragging,
    onInteractionStart: stopProgrammaticScrollAnimation,
  }
);

// ─── Mouse-drag to scroll (desktop) ─────────────────────────────────────────

let dragMoveOccurred = false;
let isDraggingClickIgnored = false;

useDragToScroll(scrollContainer, {
  onDragStart: () => {
    isProgrammaticScrolling = false;
    isDragging.value = true;
    dragMoveOccurred = false;
    cancelResume();
    stopProgrammaticScrollAnimation();
  },
  onDragEnd: () => {
    isDragging.value = false;
    snapToNearestCard();
    pauseAndScheduleResume(); // 3s delay before resuming after drag

    if (dragMoveOccurred) {
      isDraggingClickIgnored = true;
      setTimeout(() => {
        isDraggingClickIgnored = false;
      }, 50);
    }
  },
  onDragMove: () => {
    dragMoveOccurred = true;
    updateActiveIndexOnScroll();
    notifyScrolling();
  },
});

// ─── Scroll helpers ───────────────────────────────────────────────────────────

const scrollToActive = () => {
  if (!scrollContainer.value) return;
  const cards = scrollContainer.value.querySelectorAll('.phone-wrapper');
  const targetCard = cards[currentVirtualIndex.value] as HTMLElement;
  if (targetCard) {
    isProgrammaticScrolling = true;
    const targetScrollLeft = targetCard.offsetLeft - (scrollContainer.value.clientWidth - targetCard.clientWidth) / 2;
    scrollContainer.value.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth',
    });
    // Release lock after transition and sync index
    setTimeout(() => {
      isProgrammaticScrolling = false;
      updateActiveIndexOnScroll();
    }, 600);
  }
};

const updateActiveIndexOnScroll = () => {
  if (!scrollContainer.value) return;
  const containerRect = scrollContainer.value.getBoundingClientRect();
  const containerCenter = containerRect.left + containerRect.width / 2;

  const cards = scrollContainer.value.querySelectorAll('.phone-wrapper');
  let closestIndex = currentVirtualIndex.value;
  let closestDistance = Infinity;

  cards.forEach((card, index) => {
    const cardRect = card.getBoundingClientRect();
    const cardCenter = cardRect.left + cardRect.width / 2;
    const distance = Math.abs(cardCenter - containerCenter);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = index;
    }
  });

  // Check if we crossed the boundary of the middle set (5-9)
  // This must run independent of whether the index changed, since programmatic
  // scroll will update currentVirtualIndex BEFORE the scroll event starts.
  if (closestIndex < 5) {
    const card0 = cards[0] as HTMLElement;
    const card5 = cards[5] as HTMLElement;
    const setWidth = card5.offsetLeft - card0.offsetLeft;

    scrollContainer.value.classList.add('no-transition');

    // Temporarily override to instant scroll behavior to avoid browser smooth-scrolling animation during teleport
    const prevBehavior = scrollContainer.value.style.scrollBehavior;
    scrollContainer.value.style.scrollBehavior = 'auto';
    scrollContainer.value.scrollLeft = scrollContainer.value.scrollLeft + setWidth;
    scrollContainer.value.style.scrollBehavior = prevBehavior;
    currentVirtualIndex.value = closestIndex + 5;

    nextTick(() => {
      scrollContainer.value?.classList.remove('no-transition');
    });
  } else if (closestIndex >= 10) {
    const card0 = cards[0] as HTMLElement;
    const card5 = cards[5] as HTMLElement;
    const setWidth = card5.offsetLeft - card0.offsetLeft;

    scrollContainer.value.classList.add('no-transition');

    // Temporarily override to instant scroll behavior to avoid browser smooth-scrolling animation during teleport
    const prevBehavior = scrollContainer.value.style.scrollBehavior;
    scrollContainer.value.style.scrollBehavior = 'auto';
    scrollContainer.value.scrollLeft = scrollContainer.value.scrollLeft - setWidth;
    scrollContainer.value.style.scrollBehavior = prevBehavior;
    currentVirtualIndex.value = closestIndex - 5;

    nextTick(() => {
      scrollContainer.value?.classList.remove('no-transition');
    });
  } else {
    currentVirtualIndex.value = closestIndex;
  }
};

const snapToNearestCard = () => {
  if (!scrollContainer.value) return;
  updateActiveIndexOnScroll();
  scrollToActive();
};

// ─── Card & Dot navigation ──────────────────────────────────────────────────

const handleCardClick = (idx: number) => {
  if (isDraggingClickIgnored) return;
  if (!scrollContainer.value) return;

  const delta = idx - currentVirtualIndex.value;
  const targetIndex = 5 + (idx % 5);
  const tempStartIndex = targetIndex - delta;

  stopSlideTimer();

  if (tempStartIndex === currentVirtualIndex.value) {
    currentVirtualIndex.value = targetIndex;
    scrollToActive();
    startSlideTimer();
  } else {
    const cards = scrollContainer.value.querySelectorAll('.phone-wrapper');
    const tempStartCard = cards[tempStartIndex] as HTMLElement;
    if (tempStartCard) {
      // Teleport instantly to the temporary start position
      const targetScrollLeft =
        tempStartCard.offsetLeft - (scrollContainer.value.clientWidth - tempStartCard.clientWidth) / 2;

      scrollContainer.value.classList.add('no-transition');
      const prevBehavior = scrollContainer.value.style.scrollBehavior;
      scrollContainer.value.style.scrollBehavior = 'auto';
      scrollContainer.value.scrollLeft = targetScrollLeft;
      scrollContainer.value.style.scrollBehavior = prevBehavior;

      // Update virtual index to the temp start index so it keeps its "isCenter" scale state
      currentVirtualIndex.value = tempStartIndex;

      nextTick(() => {
        scrollContainer.value?.classList.remove('no-transition');
        currentVirtualIndex.value = targetIndex;
        scrollToActive();
        startSlideTimer();
      });
    } else {
      // Fallback
      currentVirtualIndex.value = targetIndex;
      scrollToActive();
      startSlideTimer();
    }
  }
};

const setIndex = (index: number) => {
  currentVirtualIndex.value = 5 + index;
  scrollToActive();
  stopSlideTimer();
  startSlideTimer();
};

// ─── Lifecycle ────────────────────────────────────────────────────────────────

onMounted(() => {
  // Center initially in middle set without animation
  if (scrollContainer.value) {
    const cards = scrollContainer.value.querySelectorAll('.phone-wrapper');
    const targetCard = cards[currentVirtualIndex.value] as HTMLElement;
    if (targetCard) {
      const targetScrollLeft = targetCard.offsetLeft - (scrollContainer.value.clientWidth - targetCard.clientWidth) / 2;
      scrollContainer.value.scrollTo({
        left: targetScrollLeft,
        behavior: 'auto',
      });
    }

    scrollContainer.value.addEventListener(
      'scroll',
      () => {
        if (!isProgrammaticScrolling) {
          updateActiveIndexOnScroll();
          // Keep pushing the resume timer while the user (or inertia) is scrolling
          notifyScrolling();
        }
      },
      { passive: true }
    );
  }

  startSlideTimer();
});

onUnmounted(() => {
  stopSlideTimer();
});
</script>

<template>
  <section class="w-full bg-white py-16">
    <div class="mx-auto max-w-360 overflow-hidden px-6 md:px-20">
      <!-- Title -->
      <h2 class="mb-14 text-center text-[26px] font-extrabold text-gray-900 sm:text-[32px] md:text-[36px]">
        {{ t('mobile.title') }}
      </h2>

      <!-- Phone mockup container wrapper to restrict visible items (5 on desktop, 3 on tablet, 1 on mobile) -->
      <div class="mx-auto w-full max-w-70 overflow-hidden sm:max-w-150 xl:max-w-250">
        <div
          ref="scrollContainer"
          class="no-scrollbar relative flex h-130 snap-x snap-mandatory items-end justify-start gap-8 overflow-x-auto px-8 pb-6 [-webkit-overflow-scrolling:touch] sm:px-48 xl:px-98"
          v-on="touchHandlers"
          @mouseenter="mouseHandlers.onEnter"
          @mouseleave="mouseHandlers.onLeave"
        >
          <div
            v-for="(app, idx) in replicatedApps"
            :key="app.uniqueId"
            class="phone-wrapper flex shrink-0 transform-gpu cursor-pointer snap-center [scroll-snap-stop:always] flex-col items-center transition-transform duration-500 ease-out hover:-translate-y-2"
            @click="handleCardClick(idx)"
          >
            <!-- Reusable phone mockup frame -->
            <HomePhoneMockup :img="app.img" :alt="app.label" :is-center="app.isCenter" />

            <!-- Label -->
            <span
              class="animate-pulse-slow mt-4 text-[13px] font-bold tracking-wider text-gray-800 uppercase transition-all duration-300"
              :class="{ 'mt-5 text-[15px] font-black text-gray-950': app.isCenter }"
            >
              {{ app.label }}
            </span>
          </div>
        </div>
      </div>

      <!-- Carousel indicator dots -->
      <div class="mt-12 flex justify-center gap-2">
        <button
          v-for="(_, index) in 5"
          :key="index"
          class="h-3 w-3 cursor-pointer rounded-full transition-all duration-300"
          :class="[currentIndex === index ? 'w-6 bg-gray-800' : 'bg-gray-400 hover:bg-gray-600']"
          :aria-label="`Go to app screen ${index + 1}`"
          @click="setIndex(index)"
        ></button>
      </div>
    </div>
  </section>
</template>

<style scoped>
.no-transition :deep(.phone-card),
.no-transition .phone-wrapper span {
  transition: none !important;
}
</style>
